import ApiClient from '@config/network';
import { useCheckoutConfigContext } from '@contexts/CheckoutConfig';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePayPalButton from '../usePayPalButton';
import Loading from '@components/Loading';
import { Center, Text, VStack } from '@chakra-ui/react';
import { DONATION_FREQUENCY, FormValues } from '@types';
import { useFormContext } from 'react-hook-form';
import { When } from 'react-if';
interface Props {
  onComplete: () => Promise<void>;
  onError: (error: string) => void;
}

const PaypalPanel: FC<Props> = ({ onComplete, onError }) => {
  const { checkout } = useCheckoutConfigContext();
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const { options: buttonOptions, loading, error } = usePayPalButton();
  const { watch } = useFormContext<FormValues>();

  const frequency = watch('frequency');

  const createOrder = useMemo(() => {
    if (frequency === DONATION_FREQUENCY.ONEOFF) {
      return async function (_data, _actions) {
        const { data } = await ApiClient.post<{ order_id: string }>(
          `rest/v1/checkout/paypal/${checkout.token}/order`,
          null,
        );

        console.log(data);
        return data.order_id;
      };
    }
  }, [frequency]);

  const createSubscription = useMemo(() => {
    if (frequency !== DONATION_FREQUENCY.ONEOFF) {
      return async function (_data, _actions) {
        const { data } = await ApiClient.post<{ subscription_id: string }>(
          `rest/v1/checkout/paypal/${checkout.token}/subscription`,
          null,
        );

        console.log(data);
        return data.subscription_id;
      };
    }
  }, [frequency]);

  if (loading || !buttonOptions) return <Loading h="200px" />;
  if (error)
    return (
      <Center>
        <Text>{error}</Text>
      </Center>
    );

  return (
    <PayPalScriptProvider
      options={{
        ...buttonOptions,
        vault: frequency === DONATION_FREQUENCY.MONTHLY ? true : undefined,
        intent: frequency === DONATION_FREQUENCY.MONTHLY ? 'subscription' : 'capture',
      }}
    >
      <PayPalButtons
        style={{ layout: 'horizontal' }}
        createOrder={createOrder}
        createSubscription={createSubscription}
        disabled={isLoading}
        onApprove={async (_data, _actions) => {
          setIsLoading(true);

          try {
            if (frequency === null || frequency === DONATION_FREQUENCY.ONEOFF) {
              await ApiClient.post<{ order_id: string }>(
                `rest/v1/checkout/paypal/${checkout.token}/order/capture`,
                null,
              );
            }

            await onComplete();
          } catch (err) {
            console.error(err);
            onError(t("We couldn't process your payment, please try again later"));
          }
        }}
      />

      <When condition={isLoading}>
        <VStack spacing={4} mt={5}>
          <Loading />
          <Text fontSize="md" fontWeight="semibold">
            {t('Processing payment, please wait...')}
          </Text>
        </VStack>
      </When>
    </PayPalScriptProvider>
  );
};

export default PaypalPanel;
